import { Injectable, inject } from '@angular/core';
import { supabase } from '../../core/supabase/supabase.client';
import {
  ImageCompressionService,
  type ImageVariantSpec,
} from './image-compression.service';

export type ImageAssetKind =
  | 'home_tab_icon'
  | 'category'
  | 'product'
  | 'collection_tile'
  | 'banner';

export const IMMUTABLE_IMAGE_CACHE_CONTROL = '31536000';

export const IMAGE_VARIANT_POLICY: Record<ImageAssetKind, ImageVariantSpec[]> = {
  home_tab_icon: [{ key: 'icon128', width: 128, quality: 0.88 }],
  category: [
    { key: 'card384', width: 384, quality: 0.82 },
    { key: 'tile768', width: 768, quality: 0.82 },
  ],
  product: [
    { key: 'thumb160', width: 160, quality: 0.82 },
    { key: 'card384', width: 384, quality: 0.82 },
    { key: 'hero1200', width: 1200, quality: 0.86 },
  ],
  collection_tile: [{ key: 'tile768', width: 768, quality: 0.82 }],
  banner: [
    { key: 'tile768', width: 768, quality: 0.82 },
    { key: 'hero1200', width: 1200, quality: 0.84 },
  ],
};

export const createImageVersion = (now = Date.now()) => `${now}-${crypto.randomUUID()}`;

export const createVersionedImageRoot = (
  basePath: string,
  version: string,
) => `${basePath}/${version}`.replace(/\/{2,}/g, '/');

const extensionForFile = (file: File) =>
  file.type === 'image/jpeg' ? 'jpg' : file.type.split('/')[1] || 'webp';

@Injectable({ providedIn: 'root' })
export class ImageAssetUploadService {
  private readonly compression = inject(ImageCompressionService);

  async upload(input: {
    file: File;
    bucket: string;
    basePath: string;
    kind: ImageAssetKind;
    entityType: string;
    entityId?: string | number | null;
    purpose: string;
    isPrimary?: boolean;
    requestId?: string;
  }) {
    if (input.requestId) {
      const { data, error } = await supabase.from('files').select('id, bucket, path, metadata')
        .eq('upload_request_id', input.requestId).maybeSingle();
      if (error) throw error;
      if (data) return {
        fileId: data.id as number,
        masterUrl: supabase.storage.from(data.bucket).getPublicUrl(data.path).data.publicUrl,
        masterPath: data.path as string, bucket: data.bucket as string,
        variants: (data.metadata?.variants ?? {}) as Record<string, string>,
        version: String(data.metadata?.version ?? ''), metadata: data.metadata,
      };
    }
    const version = createImageVersion();
    const root = createVersionedImageRoot(input.basePath, version);
    const generated = await this.compression.generateVariants(
      input.file,
      IMAGE_VARIANT_POLICY[input.kind],
    );
    const uploadedPaths: string[] = [];
    const uploadFile = async (path: string, file: File) => {
      let target = path;
      for (let attempt = 0; attempt < 3; attempt++) {
        const { error } = await supabase.storage.from(input.bucket).upload(target, file, {
          upsert: false,
          cacheControl: IMMUTABLE_IMAGE_CACHE_CONTROL,
          contentType: file.type,
        });
        if (!error) {
          uploadedPaths.push(target);
          return { path: target, url: supabase.storage.from(input.bucket).getPublicUrl(target).data.publicUrl };
        }
        const conflict = String(error.statusCode) === '409' ||
          String((error as { code?: string }).code) === 'KeyAlreadyExists';
        if (!conflict || attempt === 2) throw error;
        // Never overwrite or assume a conflicting object belongs to this file.
        // Keep the original folder so metadata and cleanup retain ownership.
        const dot = path.lastIndexOf('.');
        target = `${path.slice(0, dot)}-${crypto.randomUUID()}${path.slice(dot)}`;
      }
      throw new Error('Image upload could not complete');
    };

    try {
      const master = await uploadFile(`${root}/master.${extensionForFile(generated.master.file)}`, generated.master.file);
      const masterPath = master.path;
      const masterUrl = master.url;
      const variants: Record<string, string> = {};
      const variantMetadata: Record<string, unknown> = {};
      for (const variant of generated.variants) {
        const { path, url } = await uploadFile(`${root}/${variant.key}.${extensionForFile(variant.file)}`, variant.file);
        variants[variant.key] = url;
        variantMetadata[variant.key] = {
          bucket: input.bucket,
          path,
          url,
          width: variant.width,
          height: variant.height,
          mimeType: variant.file.type,
          size: variant.file.size,
        };
      }
      const metadata = {
        version,
        masterUrl,
        variants,
        variantMetadata,
        original: generated.original,
        hasAlpha: generated.master.hasAlpha,
      };
      const { data: fileRow, error: fileError } = await supabase
        .from('files')
        .insert({
          ...(input.requestId ? { upload_request_id: input.requestId } : {}),
          file_name: generated.master.file.name,
          bucket: input.bucket,
          path: masterPath,
          mime_type: generated.master.file.type,
          size: generated.master.file.size,
          entity_type: input.entityType,
          entity_id: input.entityId ?? null,
          purpose: input.purpose,
          is_primary: input.isPrimary ?? false,
          metadata,
        })
        .select('id')
        .single();
      if (fileError) throw fileError;
      return {
        fileId: fileRow.id as number,
        masterUrl,
        masterPath,
        bucket: input.bucket,
        variants,
        version,
        metadata,
      };
    } catch (error) {
      // A files insert may commit even when its response is lost. Never remove
      // objects belonging to a committed row; another attempt can reuse it.
      if (input.requestId) {
        const { data, error: lookupError } = await supabase.from('files')
          .select('id, bucket, path, metadata').eq('upload_request_id', input.requestId).maybeSingle();
        if (lookupError) throw error;
        if (data) {
          const unused = uploadedPaths.filter(path => !path.startsWith(data.path.slice(0, data.path.lastIndexOf('/') + 1)));
          if (unused.length) await supabase.storage.from(input.bucket).remove(unused);
          return {
            fileId: data.id as number,
            masterUrl: supabase.storage.from(data.bucket).getPublicUrl(data.path).data.publicUrl,
            masterPath: data.path as string, bucket: data.bucket as string,
            variants: (data.metadata?.variants ?? {}) as Record<string, string>,
            version: String(data.metadata?.version ?? ''), metadata: data.metadata,
          };
        }
      }
      if (uploadedPaths.length) {
        await supabase.storage.from(input.bucket).remove(uploadedPaths);
      }
      throw error;
    }
  }

  /** Idempotently adds variants to an existing canonical files row. */
  async backfillExistingFile(input: {
    id: number;
    fileName?: string | null;
    bucket: string;
    path: string;
    metadata?: Record<string, any> | null;
    kind: ImageAssetKind;
  }) {
    const requiredKeys = IMAGE_VARIANT_POLICY[input.kind].map(spec => spec.key);
    const existingVariants = input.metadata?.['variants'] ?? {};
    if (requiredKeys.every(key => Boolean(existingVariants[key]))) {
      return { metadata: input.metadata, changed: false };
    }

    const { data: blob, error: downloadError } = await supabase.storage
      .from(input.bucket)
      .download(input.path);
    if (downloadError || !blob) throw downloadError ?? new Error('Image download failed');

    const source = new File([blob], input.fileName || 'master', {
      type: blob.type || 'image/jpeg',
    });
    const generated = await this.compression.generateVariants(
      source,
      IMAGE_VARIANT_POLICY[input.kind],
    );
    const version = createImageVersion();
    const basePath = input.path.includes('/')
      ? input.path.slice(0, input.path.lastIndexOf('/'))
      : 'backfill';
    const root = createVersionedImageRoot(`${basePath}/variants`, version);
    const variants = { ...existingVariants };
    const variantMetadata = { ...(input.metadata?.['variantMetadata'] ?? {}) };

    for (const variant of generated.variants) {
      if (variants[variant.key]) continue;
      const path = `${root}/${variant.key}.${extensionForFile(variant.file)}`;
      const { error } = await supabase.storage
        .from(input.bucket)
        .upload(path, variant.file, {
          upsert: false,
          cacheControl: IMMUTABLE_IMAGE_CACHE_CONTROL,
          contentType: variant.file.type,
        });
      if (error) throw error;
      const url = supabase.storage.from(input.bucket).getPublicUrl(path).data.publicUrl;
      variants[variant.key] = url;
      variantMetadata[variant.key] = {
        bucket: input.bucket,
        path,
        url,
        width: variant.width,
        height: variant.height,
        mimeType: variant.file.type,
        size: variant.file.size,
      };
    }

    const masterUrl = supabase.storage
      .from(input.bucket)
      .getPublicUrl(input.path).data.publicUrl;
    const metadata = {
      ...(input.metadata || {}),
      version,
      masterUrl,
      variants,
      variantMetadata,
      original: input.metadata?.['original'] ?? generated.original,
      hasAlpha: generated.master.hasAlpha,
    };
    const { error: updateError } = await supabase
      .from('files')
      .update({ metadata })
      .eq('id', input.id);
    if (updateError) throw updateError;
    return { metadata, changed: true };
  }
}
